import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { CUSTOM_COURT_IMG_PLACEHOLDER_SRC } from 'constants/media';
import { getVenueAddressImage } from 'constants/venues';
import { VenueAddress } from 'utils/shared/coachBuilder';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { pluralize } from 'utils/shared/pluralize';
import { useGeoLocation } from 'hooks/useGeoLocation';
import Calendar from 'svg/Calendar';
import Check from 'svg/Check';
import ChevronLeft from 'svg/ChevronLeft';
import ChevronRight from 'svg/ChevronRight';
import Home from 'svg/Home';
import Button from 'components/Button';
import classNames from 'styles/utils/classNames';

enum PickingStates {
  OnDatePicking = 'ON_DATE_PICKING',
  OnTimePicking = 'ON_TIME_PICKING',
}

interface VenueSliderProps {
  setPickingState?: React.Dispatch<React.SetStateAction<PickingStates | null>>;
  venues: VenueAddress[];
  selectedVenues: Map<string, VenueAddress> | null;
  setSelectedVenues: React.Dispatch<React.SetStateAction<Map<string, VenueAddress>>>;
  setVenueFromMap?: React.Dispatch<React.SetStateAction<VenueAddress | null>>;
  /** Horizontal padding on the header/arrows/cards rows. Override (e.g. `''`) when the parent already provides gutters. */
  paddingClassName?: string;
  /** Extra classes per card — e.g. a width override to fit more cards per row. */
  cardClassName?: string;
}

interface VenueCardProps {
  venue: VenueAddress;
  distance: number;
  isSelected: boolean;
  customCourtImgPlaceholderSrc: string;
  handleVenueSelection: (venue: VenueAddress) => () => void;
  className?: string;
}

export default function VenueSlider({
  setPickingState,
  venues,
  setSelectedVenues,
  selectedVenues,
  setVenueFromMap,
  paddingClassName = 'px-6',
  cardClassName,
}: VenueSliderProps) {
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  // Whether the venues actually overflow the container — i.e. there's anything to scroll to.
  const [canScroll, setCanScroll] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const { centerLatitude, centerLongitude } = useGeoLocation();

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      setIsAtStart(slider.scrollLeft === 0);
      setIsAtEnd(slider.scrollLeft + slider.clientWidth >= slider.scrollWidth);
      setCanScroll(slider.scrollWidth > slider.clientWidth);
    };

    slider.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Initial check

    return () => {
      slider.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [venues.length]);

  const backToDatePicker = () => {
    setVenueFromMap?.(null);
    setPickingState?.(PickingStates.OnDatePicking);
  };

  const getIsVenueSelected = (venue: VenueAddress): boolean => !!selectedVenues?.get(venue.id);

  const handleVenueSelection = (venue: VenueAddress) => () => {
    setSelectedVenues((prevSelectedVenues) => {
      const selectedVenuesCopy = new Map(prevSelectedVenues);
      if (selectedVenuesCopy.has(venue.id)) {
        selectedVenuesCopy.delete(venue.id);
      } else {
        selectedVenuesCopy.set(venue.id, venue);
      }
      return selectedVenuesCopy;
    });
  };

  const handleLeftClick = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -sliderRef.current.clientWidth, behavior: 'smooth' });
    }
  };

  const handleRightClick = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: sliderRef.current.clientWidth, behavior: 'smooth' });
    }
  };

  return (
    <div className="mb-8">
      {setPickingState && (
        <div
          className={classNames('mb-3 flex items-center justify-between gap-1', paddingClassName)}
        >
          <h1 className="typography-product-heading-compact-desktop text-color-brand-primary">
            Book a Lesson
          </h1>
          <span onClick={backToDatePicker} className="cursor-pointer">
            <Calendar className="h-6 w-6 text-color-brand-primary max-lg:mr-6" />
          </span>
        </div>
      )}
      {canScroll && (
        <div className={classNames('mb-2 flex justify-end gap-7', paddingClassName)}>
          <Button
            onClick={handleLeftClick}
            variant="inverted"
            className={classNames('border-none', isAtStart && 'cursor-not-allowed opacity-50')}
            isInline
            disabled={isAtStart}
            iconLeft={<ChevronLeft className="h-6 w-6 cursor-pointer" />}
          />
          <Button
            onClick={handleRightClick}
            variant="inverted"
            className={classNames('border-none', isAtEnd && 'cursor-not-allowed opacity-50')}
            isInline
            disabled={isAtEnd}
            iconLeft={<ChevronRight className="h-6 w-6 cursor-pointer" />}
          />
        </div>
      )}
      <div
        className={classNames('flex gap-4 overflow-x-hidden py-1', paddingClassName)}
        ref={sliderRef}
      >
        {venues.map((venue) => {
          const isSelected = getIsVenueSelected(venue);
          return (
            <VenueCard
              key={venue.id}
              className={cardClassName}
              isSelected={isSelected}
              handleVenueSelection={handleVenueSelection}
              venue={venue}
              customCourtImgPlaceholderSrc={CUSTOM_COURT_IMG_PLACEHOLDER_SRC}
              distance={Math.round(
                calculateHaversineDistance({
                  coord1: {
                    latitude: venue.latitude || 0,
                    longitude: venue.longitude || 0,
                  },
                  coord2: {
                    latitude: centerLatitude || 0,
                    longitude: centerLongitude || 0,
                  },
                  unit: 'miles',
                }),
              )}
            />
          );
        })}
      </div>
    </div>
  );
}

export const VenueCard: React.FC<VenueCardProps> = ({
  customCourtImgPlaceholderSrc,
  venue,
  isSelected,
  handleVenueSelection,
  distance,
  className,
}) => {
  return (
    <div
      className={classNames(
        'relative flex h-full min-h-40 w-[calc(50%-24px)] shrink-0 cursor-pointer select-none flex-col overflow-hidden rounded-md text-color-text-lightmode-primary shadow-color-bg-lightmode-invert outline outline-2 outline-offset-2 transition-all dark:text-color-text-darkmode-primary dark:shadow-color-bg-darkmode-invert',
        isSelected &&
          'outline-color-border-input-darkmode dark:outline-color-border-input-lightmode',
        !isSelected && 'outline-transparent',
        className,
      )}
      onClick={handleVenueSelection(venue)}
    >
      <div
        className={classNames(
          'absolute right-3 top-3 z-10 rounded-full bg-color-bg-lightmode-primary p-1 opacity-0 transition-opacity dark:bg-color-bg-darkmode-primary',
          isSelected && 'opacity-100',
        )}
      >
        <Check className="h-5 w-5" />
      </div>
      <div className="relative h-16 overflow-hidden rounded-tl-md rounded-tr-md">
        <Image
          src={getVenueAddressImage(venue) || customCourtImgPlaceholderSrc}
          alt="venue-image"
          fill={true}
        />
      </div>
      <div className="flex flex-1 flex-col gap-1 rounded-bl-md rounded-br-md bg-color-bg-lightmode-secondary p-2 dark:bg-color-bg-darkmode-secondary">
        <span className="typography-product-button-label-xs">{venue.title || venue.address}</span>
        <div className="typography-product-text-card mt-auto flex items-start gap-1">
          {/* A typed-in address has no court data, so it carries no Indoor/Outdoor claim. */}
          {(!!venue?.indoorCourtCount || !!venue?.outdoorCourtCount) && (
            <div className="flex items-center gap-1">
              <Home className="h-[0.875rem] w-[0.875rem] shrink-0 text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder" />
              <span>{!!venue?.indoorCourtCount ? 'Indoor' : 'Outdoor'}</span>
              <span className="text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
                &bull;
              </span>
            </div>
          )}
          <div className="flex  gap-1">
            <span>
              {pluralize({
                count: distance,
                singular: 'mile',
                plural: 'miles',
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
